import { Map } from 'immutable';
import { createSelector } from 'reselect';

import * as Constants from '../constants';
import { getCurrentListenerIdFromState } from '../lib/ListenerUtils';
import { getRouterCurrentLocation } from '../lib/RouteUtils';
import { getMinutesAgo, getNow } from '../lib/TimeUtils';

// const getOverlayTypes = state => state.overlay.get('types');
// const getOverlayObjects = state => state.overlay.get('overlays');
const getOverlayPriorities = state => state.overlay.get('priorities');

export function getIsShareOrStandardModalOpen(state) {
  const overlays = state.overlay.get('overlays');
  return (
    overlays.has(
      `${Constants.Overlay.OVERLAY_TYPES.modal}_${Constants.Overlay.OVERLAY_SUBTYPES.MODALSHARE}`,
    ) ||
    overlays.has(
      `${Constants.Overlay.OVERLAY_TYPES.modal}_${Constants.Overlay.OVERLAY_SUBTYPES.MODAL_STANDARD}`,
    )
  );
}

// Overlay utilities
export function getOverlayId(overlayConfig) {
  const existingId = overlayConfig[Constants.Overlay.OVERLAY_CONFIG.ID];
  if (existingId) {
    return existingId;
  }
  const generatedId = overlayConfig[Constants.Overlay.OVERLAY_CONFIG.TYPE];
  const subType = overlayConfig[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE];

  if (overlayConfig[Constants.Overlay.OVERLAY_CONFIG.PROPS]) {
    const uid = overlayConfig[Constants.Overlay.OVERLAY_CONFIG.PROPS].uid;

    if (subType && uid) {
      return `${generatedId}_${subType}_${uid}`;
    }
  }

  if (subType) {
    return `${generatedId}_${subType}`;
  }
  return generatedId;
}

export function isOverlayType(str) {
  const keys = Object.keys(Constants.Overlay.OVERLAY_TYPES);
  for (let i = 0; i < keys.length; i++) {
    if (Constants.Overlay.OVERLAY_TYPES[keys[i]] === str) {
      return true;
    }
  }
  return false;
}

// Overlay getters
export function getOverlayById(state, id) {
  const overlay = state.overlay.getIn(['overlays', id], null);
  if (overlay && overlay.toJS) {
    return overlay.toJS();
  }
  return overlay;
}

export function getOverlayIdsOfType(state, type) {
  const types = state.overlay.get('types');
  if (!types) {
    return [];
  }
  const ids = types.get(type);
  if (ids && ids.toJS) {
    return ids.toJS();
  }
  return ids || [];
}

export function getOverlaysOfType(state, type) {
  // prefer getOverlayIdsOfType if possible
  const ids = getOverlayIdsOfType(state, type);
  const overlays = state.overlay.get('overlays');
  return ids.map(id => {
    return overlays.get(id);
  });
}

export function hasActiveOverlayOfType(state, type) {
  return !!getOverlayIdsOfType(state, type).length;
}

// Usage info getters
export function getUsageInfoByOverlayId(state, id) {
  const myUsage = state.usage.getIn([Constants.Usage.UsageKeys.OVERLAYS, id], null);
  if (myUsage) {
    return myUsage.toJS();
  }
  return {
    showCount: 0,
    sessionShowCount: 0,
    lastShownTime: 0,
  };
}

export function getUsageInfoByOverlayConfig(state, overlayConfig) {
  return getUsageInfoByOverlayId(state, getOverlayId(overlayConfig));
}

export function getShowCountOfOverlayId(state, overlayId) {
  const listenerId = getCurrentListenerIdFromState(state);
  return state.education.getIn(
    [
      Constants.ListenerExternals.ReducerKeys.LISTENERS,
      listenerId,
      overlayId,
      Constants.ListenerExternals.ReducerKeys.SHOW_COUNT,
    ],
    0,
  );
}

export function shouldShowOverlay(/* state */) {
  // we always want to show overlays for now
  return true;
}

export function shouldShowBlueBar(state) {
  const route = getRouterCurrentLocation(state);
  return route && route.pathname;
}

export function shouldShowAnchored(state) {
  return !hasActiveOverlayOfType(state, Constants.Overlay.OVERLAY_TYPES.modal);
}

export function shouldShowCoachmark(state) {
  return !hasActiveOverlayOfType(state, Constants.Overlay.OVERLAY_TYPES.modal);
}

// Overlay priority
export function getOverlayPriority(overlay) {
  if (typeof overlay === 'string') {
    // fallback if we just have an ID
    // do not rely on this unless you explicitly gave the overlay an ID
    return Constants.Overlay.OVERLAY_PRIORITIES.indexOf(overlay);
  }
  // return priority of ID if we have it
  const idPriority = Constants.Overlay.OVERLAY_PRIORITIES.indexOf(
    overlay[Constants.Overlay.OVERLAY_CONFIG.ID],
  );
  if (idPriority !== -1) {
    return idPriority;
  }

  // return priority of subType if we have it
  const subTypePriority = Constants.Overlay.OVERLAY_PRIORITIES.indexOf(
    overlay[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE],
  );
  if (subTypePriority !== -1) {
    return subTypePriority;
  }

  // otherwise return priority of type
  return Constants.Overlay.OVERLAY_PRIORITIES.indexOf(
    overlay[Constants.Overlay.OVERLAY_CONFIG.TYPE],
  );
}

export function getOverlaySubtype(overlay) {
  return overlay[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE];
}

export const getHighestPriorityOverlayIdOfType = createSelector(
  getOverlayIdsOfType,
  getOverlayPriorities,
  (idsOfType, priorities) => {
    return priorities
      .findLast(
        item => {
          return idsOfType.indexOf(item.get('id')) >= 0;
        },
        null,
        new Map({ id: null }),
      )
      .get('id');
  },
);

export function getHighestPriorityOverlay(state) {
  const overlayIdPriorityQueue = getOverlayPriorities(state).reverse();
  const overlays = state.overlay.get('overlays');
  for (let i = 0; i < overlayIdPriorityQueue.size; i++) {
    const overlayId = overlayIdPriorityQueue.getIn([i, 'id']);
    if (overlays.has(overlayId)) {
      return overlays.get(overlayId);
    }
  }
  return null;
}

function getHamburgerMenuProps(overlayConfig) {
  return overlayConfig[Constants.Overlay.OVERLAY_CONFIG.PROPS];
}

// Given the overlay type and id, return all props necessary to render the overlay
export function getOverlayProps(state, overlayConfig) {
  let overlayProps = null;
  switch (overlayConfig[Constants.Overlay.OVERLAY_CONFIG.TYPE]) {
    case Constants.Overlay.OVERLAY_TYPES.hamburgerMenu:
      overlayProps = getHamburgerMenuProps(overlayConfig);
  }
  return overlayProps;
}

export function getHighestPriorityOverlayOfType(state, type) {
  return getOverlayById(state, getHighestPriorityOverlayIdOfType(state, type));
}

export function shouldOverlaysDisableTuner(state) {
  const activeModal = getHighestPriorityOverlayOfType(state, Constants.Overlay.OVERLAY_TYPES.modal);
  if (!activeModal) {
    return false;
  }
  const subType = activeModal[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE];
  // enable the tuner for Premium welcome
  if (subType === Constants.Overlay.PremiumWelcomeModalSubtype) {
    return false;
  }

  // we have an active modal, but it might be a ModalCoachMark
  // ModalCoachMarks are toast notifications that should not disable the tuner
  return !subType || subType !== Constants.Overlay.ModalCoachmarkSubtype;
}

// Overlay Frequency limiting
export function isAnchoredOverlayWithinFrequencyCap(overlayConfig, state) {
  const usageInfo = getUsageInfoByOverlayConfig(state, overlayConfig);
  const miniCoachmarkType =
    overlayConfig[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE] ||
    overlayConfig[Constants.Overlay.OVERLAY_CONFIG.ID];
  const now = getNow();
  const lastViewingDate = usageInfo.lastShownTime ? new Date(usageInfo.lastShownTime).getTime() : 0;
  const overlayId = getOverlayId(overlayConfig);
  const showCount = getShowCountOfOverlayId(state, overlayId);
  switch (miniCoachmarkType) {
    /* once ever, after FIRST_TIME_FEEDBACK has shown once */
    case Constants.MiniCoachmark.MiniCoachmarkTypes.NOW_PLAYING_REPLAY: {
      const firstTimeFeedbackConfig = {
        [Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
          Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_FEEDBACK,
        [Constants.Overlay.OVERLAY_CONFIG.TYPE]: Constants.Overlay.OVERLAY_TYPES.anchored,
      };
      const firstTimeFeedbackId = getOverlayId(firstTimeFeedbackConfig);
      const firstTimeFeedbackShowCount = getShowCountOfOverlayId(state, firstTimeFeedbackId);
      return firstTimeFeedbackShowCount === 1 && showCount === 0;
    }
    /* once per hour */
    case Constants.MiniCoachmark.MiniCoachmarkTypes.BINGE_SKIPPER:
      return (
        !lastViewingDate ||
        getMinutesAgo(now, lastViewingDate) >=
          Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits.BINGE_SKIPPER_THROTTLE_MINUTES
      );

    /* once ever */
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_NOW_PLAYING_SEARCH:
      return (
        showCount <
        Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits.FIRST_TIME_NOW_PLAYING_SEARCH_MAX_VIEWS
      );
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_MY_STATIONS_SEARCH:
      return (
        showCount <
        Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits.FIRST_TIME_MY_STATIONS_SEARCH_MAX_VIEWS
      );
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_DISCOVERY_TUNER:
      return (
        showCount <
        Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits.FIRST_TIME_DISCOVERY_TUNER_MAX_VIEWS
      );
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_ARTIST_TAKEOVER:
      return (
        showCount <
          Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits
            .FIRST_TIME_ARTIST_TAKEOVER_MAX_VIEWS && usageInfo.sessionShowCount < 1
      );
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_ARTIST_TAKEOVER_DEEP_LINK:
      return usageInfo.sessionShowCount < 2;
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_PREMIUM_WELCOME_SEARCH:
      // premium hot / cold start welcome screen fire to open minicoach
      return true;
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_FEEDBACK:
      return (
        showCount < Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits.EDUCATION_OVERLAY_MAX_VIEWS
      );
    /* whatever the cap set by product */
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_PREMIUM_ACCESS:
      return (
        showCount <= Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits.PREMIUM_ACCESS_MAX_VIEWS
      );
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_PODCAST_NOW_PLAYING:
      return (
        showCount <
        Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits
          .FIRST_TIME_PODCAST_NOW_PLAYING_MAX_VIEWS
      );
    case Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_PODCAST_COLLECTIONS:
      return (
        showCount <
        Constants.MiniCoachmark.MiniCoachmarkFrequencyLimits
          .FIRST_TIME_PODCAST_COLLECTIONS_MAX_VIEWS
      );
    /* no cap */
    case Constants.MiniCoachmark.MiniCoachmarkTypes.SKIP_LIMIT_REACHED:
    case Constants.MiniCoachmark.MiniCoachmarkTypes.SKIP_LIMIT_REACHED_FLEX:
    case Constants.MiniCoachmark.MiniCoachmarkTypes.THUMB_DOWN:
    case Constants.MiniCoachmark.MiniCoachmarkTypes.MTUP_PLAYLIST:
    case Constants.MiniCoachmark.MiniCoachmarkTypes.REPLAY_NOT_AVAILABLE_LICENSING:
    case Constants.MiniCoachmark.MiniCoachmarkTypes.REPLAY_NOT_AVAILABLE_LICENSING_HISTORY:
      return true;

    default:
      return false;
  }
}

function isBluebarWithinFrequencyCap(state, overlayConfig) {
  if (
    overlayConfig[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE] ===
    Constants.Overlay.AccountMessageBlueBarSubtype
  ) {
    const usageInfo = getUsageInfoByOverlayConfig(state, overlayConfig);
    const messageId = overlayConfig[Constants.Overlay.OVERLAY_CONFIG.PROPS].id;
    switch (messageId) {
      case Constants.BlueBar.AccountMessageBlueBars.UPDATE_INFO_MESSAGE:
      case Constants.BlueBar.AccountMessageBlueBars.UPDATE_EMAIL_MESSAGE:
      case Constants.BlueBar.AccountMessageBlueBars.UPDATE_ZIP_CODE_MESSAGE:
      case Constants.BlueBar.AccountMessageBlueBars.INVALID_INFO_MESSAGE:
      default:
        return usageInfo.sessionShowCount < 5; // not actually a requirement, example
    }
  }
  return true;
}

export function isWithinFrequencyCap(state, overlayConfig) {
  if (
    overlayConfig[Constants.Overlay.OVERLAY_CONFIG.TYPE] ===
    Constants.Overlay.OVERLAY_TYPES.anchored
  ) {
    return isAnchoredOverlayWithinFrequencyCap(overlayConfig, state);
  }
  if (
    overlayConfig[Constants.Overlay.OVERLAY_CONFIG.TYPE] === Constants.Overlay.OVERLAY_TYPES.blueBar
  ) {
    return isBluebarWithinFrequencyCap(state, overlayConfig);
  }
  return true;
}
